/** Staging/local-only passwordless login form.
 * Posts to /api/auth/dev-login. Server returns 404 when ENVIRONMENT=production.
 * Uses a standard <form> with username+password autocomplete so Chrome offers
 * to save credentials. Password value is ignored by the server. */

import { useState } from 'react'
import { BookOpen } from 'lucide-react'
import { API_BASE } from '../lib/api-client'

export function DevLoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/auth/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error || `HTTP ${res.status}`)
      }
      // Full navigation triggers Chrome's save-password prompt.
      window.location.href = '/'
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-surface-0 overflow-hidden">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[600px] -translate-x-1/2 rounded-full bg-brand-600/8 blur-[120px]" />

      <div className="relative z-10 w-full max-w-[380px] space-y-8 px-6">
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 shadow-lg shadow-brand-600/20">
            <BookOpen className="h-7 w-7 text-white" />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-neutral-50">AgentWiki staging access</h1>
            <p className="mt-2 text-sm leading-relaxed text-neutral-400">
              Internal-only shortcut for local and staging review. Production uses the normal OAuth sign-in.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-3" autoComplete="on" aria-describedby="dev-login-helper">
          <div className="space-y-1">
            <label htmlFor="dev-login-email" className="sr-only">Email</label>
            <input
              id="dev-login-email"
              type="email"
              name="username"
              autoComplete="username"
              required
              autoFocus
              placeholder="you@local.dev"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-neutral-800 bg-surface-2 px-4 py-3 text-sm text-neutral-100 placeholder:text-neutral-600 focus:border-brand-500 focus:outline-none"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="dev-login-password" className="sr-only">Password (not validated on staging)</label>
            <input
              id="dev-login-password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              placeholder="placeholder credential (not checked here)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-neutral-800 bg-surface-2 px-4 py-3 text-sm text-neutral-100 placeholder:text-neutral-600 focus:border-brand-500 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 px-4 py-3 text-sm font-medium text-white shadow-lg shadow-brand-600/20 hover:opacity-90 disabled:opacity-50"
          >
            {loading ? 'Opening staging workspace…' : 'Open staging workspace'}
          </button>
          {error && <p role="alert" className="text-xs text-red-400">{error}</p>}
        </form>

        <p id="dev-login-helper" className="text-center text-xs leading-relaxed text-neutral-600">
          Local and staging only. The server rejects this route in production.
        </p>
      </div>
    </div>
  )
}
